/**
 * Opportunity Detail Page
 *
 * Complete detail view for a single opportunity record with tabs
 */

import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { persistedOpportunityRecords } from '../../services/mockData/opportunities';
import { getRecordById, deleteRecord } from '../../services/recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { DetailPageLayout } from '../../components/detail/DetailPageLayout';
import { DetailHeader } from '../../components/detail/DetailHeader';
import { DetailTabs } from '../../components/detail/DetailTabs';
import { LayoutSection } from '../../components/detail/LayoutSection';
import { ActivityTab } from '../../components/detail/ActivityTab';
import { usePageLayout, usePageLayoutTabs } from '../../hooks/useObjectManager';
import { OpportunityModal } from '../../components/modals/OpportunityModal';
import { LogCallModal } from '../../components/modals/LogCallModal';
import { LogMeetingModal } from '../../components/modals/LogMeetingModal';
import { LogTaskModal } from '../../components/modals/LogTaskModal';
import { AddNoteModal } from '../../components/modals/AddNoteModal';
import { SendEmailModal } from '../../components/modals/SendEmailModal';
import { Mail, Target } from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface OpportunityDetailPageProps {
  id: string;
  onBack: () => void;
}

export const OpportunityDetailPage: React.FC<OpportunityDetailPageProps> = ({ id, onBack }) => {
  const { success } = useToast();
  const queryClient = useQueryClient();
  const tabs = usePageLayoutTabs('Opportunity__c');
  const [activeTab, setActiveTab] = useState(tabs[0]?.id ?? 'overview');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showCallModal, setShowCallModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const { data: opportunity, isLoading } = useQuery({
    queryKey: ['opportunity', id],
    queryFn: async () => {
      if (shouldUseGobodo()) {
        const record = await getRecordById('Opportunity', id);
        if (!record) throw new Error('Opportunity not found');
        return record;
      }
      await new Promise(resolve => setTimeout(resolve, 300));
      const allOpportunities = persistedOpportunityRecords.get();
      const found = allOpportunities.find(o => o.id === id);
      if (!found) throw new Error('Opportunity not found');
      return found;
    }
  });

  // Fetch page layout (uses Record Pages assignment system)
  const { data: pageLayout, isLoading: loadingLayout } = usePageLayout('Opportunity__c', opportunity);

  const handleEdit = () => {
    setIsModalOpen(true);
  };

  const handleDelete = () => {
    const displayName = (opportunity as any)?.Name__c ?? id;
    if (!window.confirm(`Are you sure you want to delete ${displayName}?`)) return;
    if (shouldUseGobodo()) {
      deleteRecord('Opportunity', id)
        .then((ok) => {
          if (ok) {
            queryClient.invalidateQueries({ queryKey: ['opportunityListViewData'] });
            queryClient.invalidateQueries({ queryKey: ['gobodoRecords', 'Opportunity__c'] });
            success('Opportunity deleted', `${displayName} has been deleted`);
            onBack();
          }
        })
        .catch(() => success('Error', 'Failed to delete opportunity'));
      return;
    }
    const allOpportunities = persistedOpportunityRecords.get();
    persistedOpportunityRecords.set(allOpportunities.filter(o => o.id !== id));
    success('Opportunity deleted', `${displayName} has been deleted`);
    onBack();
  };

  if (isLoading) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Opportunities">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DetailPageLayout>
    );
  }

  if (!opportunity) {
    return (
      <DetailPageLayout onBack={onBack} backLabel="Back to Opportunities">
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">Opportunity not found</p>
        </div>
      </DetailPageLayout>
    );
  }

  // Stage badge colors
  const stageColors: Record<string, string> = {
    'Meeting Set': 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    'Discovery': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    'Pitch': 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    'Evaluation': 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    'Pending Deployment': 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
    'Closed Won': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    'Closed Lost': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  };

  // Field accessors using __c naming convention (PE-1461)
  const oppName = (opportunity as any).Name__c ?? '';
  const oppClientName = (opportunity as any).ClientName__c ?? '';
  const oppType = (opportunity as any).Type__c ?? 'Opportunity';
  const oppStage = (opportunity as any).Stage__c ?? 'Meeting Set';
  const oppAmount = parseFloat((opportunity as any).Amount__c) || 0;
  const oppProbability = parseFloat((opportunity as any).Probability__c) || 0;
  const oppCloseDate = (opportunity as any).CloseDate__c ?? '';
  const oppContactName = (opportunity as any).ContactName__c ?? oppClientName;

  // Format close date safely
  const formatCloseDate = (dateStr: string): string => {
    if (!dateStr) return 'Not set';
    const date = new Date(dateStr);
    return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString();
  };

  const isOverview = ['overview', 'tab_001', 'details'].includes(activeTab);
  const isPipeline = ['pipeline'].includes(activeTab);
  const isActivity = ['activity', 'tab_003'].includes(activeTab);

  return (
    <DetailPageLayout onBack={onBack} backLabel="Back to Opportunities">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <DetailHeader
          title={oppName}
          subtitle={`${oppClientName} • ${oppType}`}
          recordId={opportunity.id as string}
          status={oppStage}
          statusColor={stageColors[oppStage] || stageColors['Meeting Set']}
          onEdit={handleEdit}
          onDelete={handleDelete}
          additionalActions={
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Mail className="w-4 h-4" />
              Send Email
            </button>
          }
        />

        {/* Key Metrics Banner */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Amount */}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Amount</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(oppAmount)}
              </p>
            </div>

            {/* Probability */}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Probability</p>
              <div className="flex items-center gap-3">
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {oppProbability}%
                </p>
                <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all"
                    style={{ width: `${oppProbability}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Close Date */}
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Close Date</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCloseDate(oppCloseDate)}
              </p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <DetailTabs
            tabs={tabs}
            activeTab={activeTab}
            onChange={setActiveTab}
          />

          <div className="p-6">
            {isOverview && (
              <div className="space-y-6">
                {loadingLayout ? (
                  // Loading state
                  <div className="space-y-6">
                    {[1, 2, 3].map(i => (
                      <div
                        key={i}
                        className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-6"
                      >
                        <div className="animate-pulse space-y-3">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  // Render sections from page layout
                  pageLayout?.sections.map(section => (
                    <LayoutSection
                      key={section.id}
                      section={section}
                      objectApiName="Opportunity"
                      record={opportunity}
                    />
                  ))
                )}
              </div>
            )}

            {isPipeline && (
              <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
                <div className="text-center">
                  <Target className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                    Pipeline Progression
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Stage progression visualization and historical tracking will be available soon.
                  </p>
                </div>
              </div>
            )}

            {isActivity && (
              <ActivityTab
                recordId={opportunity.id as string}
                recordType="opportunity"
                onLogActivity={(type) => {
                  if (type === 'call') setShowCallModal(true);
                  if (type === 'meeting') setShowMeetingModal(true);
                  if (type === 'task') setShowTaskModal(true);
                  if (type === 'note') setShowNoteModal(true);
                }}
              />
            )}
          </div>
        </div>
      </div>

      {/* Opportunity Edit Modal */}
      <OpportunityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        opportunity={opportunity}
      />

      {/* Log Call Modal */}
      <LogCallModal
        isOpen={showCallModal}
        onClose={() => setShowCallModal(false)}
        recordId={opportunity.id as string}
        recordType="opportunity"
        contactName={oppContactName}
      />

      {/* Log Meeting Modal */}
      <LogMeetingModal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        recordId={opportunity.id as string}
        recordType="opportunity"
      />

      {/* Log Task Modal */}
      <LogTaskModal
        isOpen={showTaskModal}
        onClose={() => setShowTaskModal(false)}
        recordId={opportunity.id as string}
        recordType="opportunity"
        contactName={oppName}
      />

      {/* Add Note Modal */}
      <AddNoteModal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        recordId={opportunity.id as string}
        recordType="opportunity"
      />

      {/* Send Email Modal */}
      <SendEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recordId={opportunity.id as string}
        recordType="opportunity"
        recipientEmail={undefined}
        recipientName={oppContactName}
      />
    </DetailPageLayout>
  );
};
